/**
 * The browser half of passkeys: turn the server's JSON options (base64url
 * strings) into what `navigator.credentials` takes, and the credential it
 * returns back into JSON the server reads (webauthn.ts). No library — the
 * conversion is the whole job.
 */

const fromB64u = (s: string): ArrayBuffer => {
  const pad = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(s.length / 4) * 4, "=");
  const bin = atob(pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
};
const toB64u = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/** True when this browser can use passkeys at all. */
export function passkeysSupported(): boolean {
  return typeof window !== "undefined" && typeof window.PublicKeyCredential === "function" && Boolean(navigator.credentials);
}

type Json = Record<string, unknown>;
const descriptors = (list: unknown): PublicKeyCredentialDescriptor[] =>
  (Array.isArray(list) ? list : []).map((d: Json) => ({ type: "public-key", id: fromB64u(String(d["id"])), ...(Array.isArray(d["transports"]) ? { transports: d["transports"] as AuthenticatorTransport[] } : {}) }));

export function creationOptions(o: Json): PublicKeyCredentialCreationOptions {
  const user = o["user"] as Json;
  return {
    challenge: fromB64u(String(o["challenge"])),
    rp: o["rp"] as PublicKeyCredentialRpEntity,
    user: { id: fromB64u(String(user["id"])), name: String(user["name"]), displayName: String(user["displayName"]) },
    pubKeyCredParams: o["pubKeyCredParams"] as PublicKeyCredentialParameters[],
    authenticatorSelection: o["authenticatorSelection"] as AuthenticatorSelectionCriteria,
    excludeCredentials: descriptors(o["excludeCredentials"]),
    attestation: "none",
    timeout: Number(o["timeout"] ?? 300_000),
  };
}

export function requestOptions(o: Json): PublicKeyCredentialRequestOptions {
  return {
    challenge: fromB64u(String(o["challenge"])),
    rpId: String(o["rpId"]),
    userVerification: "required",
    allowCredentials: descriptors(o["allowCredentials"]),
    timeout: Number(o["timeout"] ?? 300_000),
  };
}

/** A new passkey, as the server's register/verify expects it. */
export function creationJson(cred: PublicKeyCredential): Json {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: toB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: toB64u(r.clientDataJSON),
      attestationObject: toB64u(r.attestationObject),
      transports: typeof r.getTransports === "function" ? r.getTransports() : [],
    },
  };
}

/** A passkey sign-in, as the server's login/verify expects it. */
export function assertionJson(cred: PublicKeyCredential): Json {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: toB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: toB64u(r.clientDataJSON),
      authenticatorData: toB64u(r.authenticatorData),
      signature: toB64u(r.signature),
      userHandle: r.userHandle ? toB64u(r.userHandle) : null,
    },
  };
}

/** A friendly default name for a new passkey, from the device it is made on. */
export function defaultPasskeyName(ua: string = typeof navigator !== "undefined" ? navigator.userAgent : ""): string {
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/Mac OS X/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  if (/Linux/.test(ua)) return "Linux computer";
  return "Passkey";
}

/** Map a WebAuthn DOMException to a sentence a person can act on. */
export function passkeyErrorMessage(e: unknown): string {
  const name = e instanceof Error ? e.name : "";
  if (name === "NotAllowedError") return "The passkey prompt was closed or timed out.";
  if (name === "InvalidStateError") return "This device already has a passkey for your account.";
  if (name === "SecurityError") return "Passkeys need the site to be opened over HTTPS on its own address.";
  if (name === "NotSupportedError") return "This browser or device can't make a passkey.";
  return e instanceof Error ? e.message : String(e);
}
